import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Rocket, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('token') || '';

export const FirstStepsChecklist = ({ companyId, totalFeedbacks = 0, theme }) => {
  const key = `fcod_first_steps_${companyId || 'anon'}`;
  const [manual, setManual] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; }
  });
  const [company, setCompany] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/company/settings`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setCompany(res.data);
    } catch { /* silencioso: o checklist simplesmente nao aparece */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const mark = (id) => {
    const next = { ...manual, [id]: true };
    setManual(next);
    localStorage.setItem(key, JSON.stringify(next));
  };

  if (!company || hidden) return null;

  const s = company.settings || {};
  const steps = [
    {
      id: 'placa',
      label: 'Imprimir a placa com QR Code',
      hint: 'Coloque no balcão, na altura dos olhos.',
      done: !!company.has_downloaded_material || !!manual.placa,
      to: '/settings?tab=qrcode',
      cta: 'Gerar placa',
    },
    {
      id: 'teste',
      label: 'Fazer uma avaliação de teste',
      hint: 'Veja como o cliente enxerga a sua tela.',
      done: totalFeedbacks > 0 || !!manual.teste,
      to: `/${company.slug}`,
      cta: 'Abrir meu link',
      external: true,
    },
    {
      id: 'google',
      label: 'Conferir o link do Google',
      hint: 'Sem ele, o cliente satisfeito não avalia no Google.',
      done: !!(s.google_maps_url || '').trim() || !!manual.google,
      to: '/settings?tab=brand',
      cta: 'Configurar',
    },
    {
      id: 'whatsapp',
      label: 'Configurar o WhatsApp de alertas',
      hint: 'É por onde você recebe o aviso de cada avaliação.',
      done: !!(s.contact_whatsapp || '').trim() || !!manual.whatsapp,
      to: '/settings?tab=brand',
      cta: 'Configurar',
    },
  ];

  const feitos = steps.filter(st => st.done).length;
  if (feitos === steps.length) return null;

  const dm = theme?.dm;

  return (
    <div
      className={`rounded-xl p-5 mb-6 border ${dm ? 'bg-slate-900/60 border-slate-700' : 'bg-white border-slate-200'}`}
      data-testid="first-steps-checklist"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5 text-[#C5A059]" />
          <div>
            <h3 className={`font-bold ${dm ? 'text-white' : 'text-[#0A2647]'}`}>Primeiros passos</h3>
            <p className="text-xs text-slate-400">{feitos} de {steps.length} concluídos · some sozinho quando terminar</p>
          </div>
        </div>
        <button onClick={() => setHidden(true)} className="p-1 rounded hover:bg-slate-100/10" title="Esconder por agora" data-testid="hide-checklist">
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      <div className="space-y-2">
        {steps.map(st => (
          <div
            key={st.id}
            className={`flex items-center gap-3 p-3 rounded-lg ${st.done ? (dm ? 'bg-emerald-950/30' : 'bg-emerald-50') : (dm ? 'bg-slate-800/60' : 'bg-slate-50')}`}
            data-testid={`step-${st.id}`}
          >
            {st.done
              ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              : <Circle className="w-5 h-5 text-slate-300 flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${st.done ? 'text-emerald-700 line-through' : (dm ? 'text-slate-100' : 'text-slate-800')}`}>{st.label}</p>
              {!st.done && <p className="text-xs text-slate-400">{st.hint}</p>}
            </div>
            {!st.done && (
              <div className="flex items-center gap-1 flex-shrink-0">
                {st.external ? (
                  <a
                    href={st.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => mark(st.id)}
                    className="text-xs font-bold text-[#0A2647] bg-[#C5A059] px-3 py-1.5 rounded-lg hover:bg-[#d4af61]"
                    data-testid={`step-cta-${st.id}`}
                  >
                    {st.cta}
                  </a>
                ) : (
                  <Link
                    to={st.to}
                    className="text-xs font-bold text-[#0A2647] bg-[#C5A059] px-3 py-1.5 rounded-lg hover:bg-[#d4af61]"
                    data-testid={`step-cta-${st.id}`}
                  >
                    {st.cta}
                  </Link>
                )}
                <button
                  onClick={() => mark(st.id)}
                  className="text-xs text-slate-400 hover:text-slate-600 px-2"
                  title="Já fiz isso"
                  data-testid={`step-done-${st.id}`}
                >
                  já fiz
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FirstStepsChecklist;
